import React, { useEffect, useState } from 'react'
import { useLocation } from '@reach/router'
import Button from '@mui/material/Button'
import ParrotfishIcon from '../ParrotfishIcon'
import { congizyScriptProps } from './types'
import { useConsent } from '../../hooks/useConsent'
import { IS_PROD } from '../../utils/constants'
import CookieComplianceWrapper from '../CookieComplianceWrapper'

declare global {
  interface Window {
    CognigyWebchat?: string
  }
}
export default function CognigyChatInitializer({
  isChatCTA,
  buttonText,
  trackingId: id,
  icon,
  inverted = false,
  variant,
  color,
  ...props
}: congizyScriptProps) {
  const [isChatEnabled, setIsChatEnabled] = useState<boolean>(false)
  const location = useLocation()
  const { cookie_cat_functional } = useConsent()
  const { cookie_cat_statistic } = useConsent()

  const COGNIGY_URL = IS_PROD
    ? 'https://endpoint-yousee.cognigy.cloud/f804c36e145ee11add28297c59e7d4ac9ae13d82b05aca71b7d8d3e58d9dbf72'
    : 'https://endpoint-yousee.cognigy.cloud/8621cb91f7ca68716719d02519d4175cc1ed7fd23f721a988c1e067442471aad'
  useEffect(() => {
    const pagesWithChat = ['/kundeservice/internet/', '/kundeservice/faktura/']

    // Check if the current pathname starts with any of the allowed paths
    const isChatPage = pagesWithChat.some(page => location.pathname.startsWith(page))
    if (isChatPage && cookie_cat_functional && cookie_cat_statistic) {
      loadCognigyScript()
    } else {
      removeCognigyScript()
    }

    // Cleanup when the component location is change
    return () => {
      removeCognigyScript()
    }
  }, [location.pathname, cookie_cat_functional, cookie_cat_statistic])

  const loadCognigyScript = () => {
    if (
      !document.querySelector('script[src="https://github.com/Cognigy/Webchat/releases/latest/download/webchat.js"]')
    ) {
      const script = document.createElement('script')
      script.src = 'https://github.com/Cognigy/Webchat/releases/latest/download/webchat.js'
      script.async = true
      script.onload = () => {
        const initScript = document.createElement('script')
        initScript.setAttribute('data-cognigy', 'init')

        // Set the title as empty in case of connectivity issues by setting 'inform' mode
        initScript.innerHTML = `initWebchat("${COGNIGY_URL}",
          {
            settings: {
              unreadMessages :{
                enableIndicator: false 
             }
            }
          }
        )`
        document.head.appendChild(initScript)
      }
      document.head.appendChild(script)
    }

    if (!document.querySelector('meta[name="cognigy-webchat"]')) {
      const metaTag = document.createElement('meta')
      metaTag.name = 'cognigy-webchat'
      metaTag.content = 'active'
      document.head.appendChild(metaTag)
    }
    setIsChatEnabled(true)
  }

  const removeCognigyScript = () => {
    const webchatScript = document.querySelector(
      'script[src="https://github.com/Cognigy/Webchat/releases/latest/download/webchat.js"]',
    )
    if (webchatScript) {
      document.head.removeChild(webchatScript)
    }

    const initScript = document.querySelector('script[data-cognigy="init"]')
    if (initScript) {
      document.head.removeChild(initScript)
    }

    const webchatContainer = document.querySelector('[data-cognigy-webchat-root]')
    if (webchatContainer) {
      webchatContainer.remove()
    }

    const toggleButton = document.querySelector('[data-cognigy-webchat-toggle]')
    if (toggleButton) {
      toggleButton.remove()
    }

    const metaTag = document.querySelector('meta[name="cognigy-webchat"]')
    if (metaTag) {
      document.head.removeChild(metaTag)
    }
    delete window['CognigyWebchat']
    setIsChatEnabled(false)
  }

  //Trigger Cognizy Chat using query selector
  const handleStartChat = () => {
    const chatButton = document.querySelector('[data-cognigy-webchat-toggle]') as HTMLElement
    if (chatButton) {
      chatButton.click()
    } else {
      console.warn('Cognigy chat button not found.')
    }
  }

  //CTAButton changes to support new type ctaTYpe = 'ChatCTA'
  if (inverted && variant === 'outlined') variant = 'invertedOutlined'
  if (inverted && variant === 'text') variant = 'invertedText'
  const colorMap = new Map()
  colorMap.set('red', 'secondary')
  colorMap.set('green', 'primary')
  colorMap.set('info', 'info')

  return (
    <>
      {isChatCTA && isChatEnabled && (
        <CookieComplianceWrapper cookieConsent={['Functional', 'Statistic']}>
          <Button
            {...props}
            {...(color && { color: colorMap.get(color) })}
            id={id || ''}
            variant={variant ?? 'contained'}
            startIcon={icon ? <ParrotfishIcon {...icon} /> : props.startIcon}
            onClick={handleStartChat}
          >
            {buttonText}
          </Button>
        </CookieComplianceWrapper>
      )}
    </>
  )
}
